/**
 * Check-out sheet: records WHO collected a student.
 *
 * The underlying data model still calls this a pickup (`markPickedUp`, `authorized_pickups`,
 * the `picked_up` status), and those names are left alone. Only what the teacher reads is
 * phrased as check-out, so the portal's language is consistent end to end.
 */
import { createPortal } from 'react-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, AlertTriangle } from 'lucide-react';
import { useAuthorizedPickups, useMarkPickedUp } from '../../hooks/useAttendance';
import { supabase } from '../supabase';
import { isSampleId } from '../sample/sampleData';
import { sampleGuardians } from '../sample/sampleStore';
import { StudentAvatar } from '../../components/StudentAvatar';
import { useStudentPhotoUrls } from '../../hooks/useStudentPhotoUrls';

interface Props {
  recordId: string;
  studentName: string;
  studentId: string;
  /**
   * Signed URL for the student's headshot, already fetched by the roster. Passed in rather
   * than re-signed here so opening the sheet costs no network round trip — the teacher has
   * an adult waiting in front of them.
   */
  photoUrl?: string | null;
  /**
   * Tapping a face asks the roster to enlarge it, since the roster owns the lightbox. Carries
   * its own subject: the sheet shows the child at the top and the authorized adults below,
   * and the enlarged view has to name whichever one was tapped.
   */
  onEnlargePhoto?: (photo: { url: string; name: string; subtitle?: string | null }) => void;
  programId: string;
  sessionDate: string;
  /** Staff member releasing the student — recorded alongside who collected them. */
  recordedByName?: string | null;
  onClose: () => void;
}

interface CheckOutOption {
  key: string;
  name: string;
  label: string | null; // relationship or tag
  /**
   * Object path for this adult's photo, or null. Only authorized-pickup contacts can have
   * one — a linked parent has no photo anywhere in the system, and shows initials.
   */
  photoPath: string | null;
}

interface ParentLinkRow {
  relationship: string | null;
  parents: { first_name: string; last_name: string } | null;
}

export function CheckOutModal({ recordId, studentName, studentId, photoUrl, onEnlargePhoto, programId, sessionDate, recordedByName, onClose }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [freetext, setFreetext] = useState('');
  const [showFreetext, setShowFreetext] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parents/guardians already linked to the student — always authorized
  const { data: parentLinks = [], isLoading: parentsLoading } = useQuery<ParentLinkRow[]>({
    queryKey: ['student-parent-links', studentId],
    queryFn: async () => {
      // Sample students have made-up families that live only in this browser.
      if (isSampleId(studentId)) {
        return sampleGuardians(studentId).guardians.map(g => ({
          relationship: g.relationship,
          parents: {
            first_name: g.name.split(' ')[0],
            last_name: g.name.split(' ').slice(1).join(' '),
          },
        }));
      }
      const { data, error: err } = await supabase
        .from('student_parents')
        .select('relationship, parents(first_name, last_name)')
        .eq('student_id', studentId);
      if (err) throw err;
      return (data ?? []) as unknown as ParentLinkRow[];
    },
    enabled: Boolean(studentId),
  });

  // Additional pickups explicitly added by the parent
  const { data: extraPickups = [], isLoading: pickupsLoading } = useAuthorizedPickups(studentId);

  const markPickedUp = useMarkPickedUp();
  const isLoading = parentsLoading || pickupsLoading;

  // Build the combined authorized list:
  // 1. Parents/guardians (de-duplicated by name)
  // 2. Explicitly added pickups whose name isn't already in the parent list
  const parentOptions: CheckOutOption[] = parentLinks
    .filter(pl => pl.parents)
    .map(pl => ({
      key: `parent-${pl.parents!.first_name}-${pl.parents!.last_name}`,
      name: `${pl.parents!.first_name} ${pl.parents!.last_name}`,
      label: pl.relationship ?? 'Parent / Guardian',
      photoPath: null,
    }));

  const parentNames = new Set(parentOptions.map(p => p.name.toLowerCase()));

  const extraOptions: CheckOutOption[] = extraPickups
    .filter(ep => !parentNames.has(ep.name.toLowerCase()))
    .map(ep => ({
      key: `extra-${ep.id}`,
      name: ep.name,
      label: ep.relationship ?? null,
      photoPath: ep.photoPath,
    }));

  const allOptions: CheckOutOption[] = [...parentOptions, ...extraOptions];
  const noOptions = !isLoading && allOptions.length === 0;

  // Signed here rather than prefetched with the roster's headshots: the roster would have to
  // load every child's pickup list to know these paths, and only one child's list is ever
  // needed at a time. One extra round trip when the sheet opens, alongside the two it already
  // makes, and the faces land with the names.
  const { data: pickupPhotoUrls = {} } = useStudentPhotoUrls(extraPickups.map(p => p.photoPath));

  // When no selectable options exist, treat freetext as the active path
  const effectiveName = (showFreetext || noOptions) ? freetext.trim() : (selected ?? '');
  const isAuthorized = !showFreetext && !noOptions && selected !== null;

  async function handleConfirm() {
    if (!effectiveName) { setError('Please enter a name.'); return; }
    setError(null);
    try {
      await markPickedUp.mutateAsync({ recordId, programId, sessionDate, pickedUpByName: effectiveName, isAuthorized, recordedByName, studentId, studentName });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record check-out.');
    }
  }

  // Portalled to `document.body` for the reason under `MOBILE_CHROME_ON_BODY` in
  // `StaffLayout`: the page renders inside pull-to-refresh's `overflow: hidden` wrappers, and
  // on iOS a clip in the ancestry keeps a fixed element out of the viewport-fixed layer that
  // the bottom nav and the voice button now live in. Left in the roster, this sheet painted
  // *under* both of them — its Confirm and Cancel buttons sat behind the nav — no matter what
  // z-index it carried, because the two are not in the same layer to compare within.
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex flex-col justify-end sm:items-center sm:justify-center z-[60]"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-xl p-6 w-full sm:max-w-md sm:mx-4 space-y-4">
        <div className="sm:hidden flex justify-center -mt-2 mb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {/* Bigger than the roster's avatar on purpose: this is the moment the child is
                actually handed over, and it is the last chance to notice a mismatch. */}
            <StudentAvatar
              name={studentName}
              photoUrl={photoUrl}
              size="lg"
              onEnlarge={
                photoUrl && onEnlargePhoto
                  ? () => onEnlargePhoto({ url: photoUrl, name: studentName })
                  : undefined
              }
            />
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">Check Out</h3>
              <p className="text-sm text-gray-500 mt-0.5 break-words">{studentName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 animate-pulse rounded-lg" />)}
          </div>
        ) : allOptions.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Checked out by</p>
            <div className="space-y-1.5">
              {allOptions.map(opt => {
                const optPhotoUrl = opt.photoPath ? pickupPhotoUrls[opt.photoPath] ?? null : null;
                return (
                  // The avatar is a button of its own when there is a face to enlarge, so the
                  // row cannot be one — the selection target is the label beside it instead.
                  <div
                    key={opt.key}
                    className={`flex items-center gap-2.5 pl-2.5 rounded-lg border text-sm transition-colors ${
                      selected === opt.name && !showFreetext
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <StudentAvatar
                      name={opt.name}
                      photoUrl={optPhotoUrl}
                      size="sm"
                      onEnlarge={
                        optPhotoUrl && onEnlargePhoto
                          ? () => onEnlargePhoto({
                              url: optPhotoUrl,
                              name: opt.name,
                              subtitle: `Authorized to collect ${studentName}`,
                            })
                          : undefined
                      }
                    />
                    <button
                      onClick={() => { setSelected(opt.name); setShowFreetext(false); setError(null); }}
                      className="flex-1 text-left py-2.5 pr-3 min-w-0"
                    >
                      <span className="font-medium">{opt.name}</span>
                      {opt.label && <span className="text-gray-400 ml-1.5">· {opt.label}</span>}
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => { setShowFreetext(true); setSelected(null); setError(null); }}
              className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                showFreetext
                  ? 'border-amber-400 bg-amber-50 text-amber-900'
                  : 'border-gray-200 hover:border-gray-300 text-gray-500'
              }`}
            >
              Someone else…
            </button>
          </div>
        ) : null}

        {(showFreetext || noOptions) && (
          <div>
            {!showFreetext && (
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Checked out by</p>
            )}
            <input
              type="text"
              autoFocus
              value={freetext}
              onChange={e => { setFreetext(e.target.value); setError(null); }}
              placeholder="Enter name"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {(showFreetext || noOptions) && freetext.trim() && (
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                <span>Not on the authorized list.</span>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={() => void handleConfirm()}
            disabled={!effectiveName || markPickedUp.isPending}
            className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {markPickedUp.isPending ? 'Saving…' : 'Confirm Check-out'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
